import { FunctionComponent, useState, useEffect, useRef, useMemo, useCallback } from 'react'
import type React from 'react'
import styled from 'styled-components'
import * as pdfjsLib from 'pdfjs-dist'
import { IWorkspacePost, usePostsHandlers } from '../../../../services/actions/posts'
import Text from '../../../../components/Text'
import ProfilePhoto from '../../../../components/ProfilePhoto'
import Row from '../../../../components/Row'
import Column from '../../../../components/Column'
import Select from '../../../../components/select/Select'
import FormControl from '../../../../components/FormControl'
import { styles } from '../../../../styles/themes/style'
import PollDisplay from '../../../../components/PollDisplay'
import SvgFragment from '../../../../fragments/SvgFragment'
import { formatTimeAgo, normalizeMessageForTextarea, replacePlaceholders, autosizeTextarea, convertEmoticonsToEmoji, isLNFirst } from '../../../../utils'
import { useAppHandlers } from '../../../../services/actions/app/useAppHandlers'
import { useAuthStore } from '../../../../stores/auth/useAuthStore'
import Assistant from '../../../../fragments/assistant/Assistant'
import LoadingSpinner from '../../../../components/LoadingSpinner'
import { EmojiPicker } from '../../../../fragments/Emoji'
import ConnectionActionWithFeedsDropdown from '../../../../fragments/dropdowns/ConnectionActionWithFeedsDropdown'
import { IConnection } from '../../../../interfaces/models/connection'
import TooltipWrapper from '../../../../components/TooltipWrapper'
import ActionText from '../../../../components/ActionText'
import EditDeleteActionDropdown from '../../../../fragments/dropdowns/EditDeleteActionDropdown'
import { useAppStore } from '../../../../stores/application/useAppStore'
import { SidebarTypes } from '../../../../interfaces/store/appStore'
import { useConnectionsHandlers } from '../../../../services/actions/connections/useConnectionsHandlers'
import { useExternalHandlers } from '../../../../services/actions/navigation/useExternalHandlers'
import { useExtensionRequest } from '../../../../services/apis/extension/useExtensionRequest'

interface IPostCardProps {
    post: IWorkspacePost
    onPostHandler?: (postId: string, handler: string, reacted: string | null, num_likes: number | null, num_comments: number | null) => void
    currentFeedId?: string
    onFeedUpdate?: (updatedConnection: IConnection) => void
    onCreateFeed?: (connectionId: string) => void
    searchValues?: Array<{
        uid: string;
        searchValue: string;
        isOutterValueSearchOr: string;
    }>
}

const PostCard: FunctionComponent<IPostCardProps> = ({ post, onPostHandler, currentFeedId, onFeedUpdate, onCreateFeed, searchValues = [] }) => {
    const { profileInfo, posts } = post
    const postData = (posts as any)?.post // Updated path to post data
    const [commentText, setCommentText] = useState('')
    const [showHoverPopup, setShowHoverPopup] = useState(false)
    const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null)
    const [showReplyDropdown, setShowReplyDropdown] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const commentInputRef = useRef<HTMLTextAreaElement>(null)
    const [localSparkleLoading, setLocalSparkleLoading] = useState<boolean>(false)
    const [isExpanded, setIsExpanded] = useState<boolean>(false)
    const [showExpandButton, setShowExpandButton] = useState<boolean>(false)
    const contentRef = useRef<HTMLDivElement>(null)
    const [currentSlide, setCurrentSlide] = useState<number>(0)
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false)
    const [touchStart, setTouchStart] = useState<number | null>(null)
    const [touchEnd, setTouchEnd] = useState<number | null>(null)
    const [pdfPages, setPdfPages] = useState<string[]>([])
    const [isPdfLoading, setIsPdfLoading] = useState<boolean>(false)
    const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([])
    const { store: { isLoggedToLinkedin } } = useAuthStore()
    const { store: { extensionInstalled, extensionVersion } } = useAppStore()
    const [commentsSortBy, setCommentsSortBy] = useState<any>({
        value: "newest",
        label: <Text $label>{"Most recent"}</Text>
    })
    const [localComments, setLocalComments] = useState<any[]>([])
    const [visibleCommentsCount, setVisibleCommentsCount] = useState<number>(1)
    const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set())
    const [editingCommentId, setEditingCommentId] = useState<string | null>(null)
    const [editingCommentText, setEditingCommentText] = useState<string>('')
    const [editCommentSparkleLoading, setEditCommentSparkleLoading] = useState<boolean>(false)
    const [loadingConversation, setLoadingConversation] = useState<string | null>(null)
    const editCommentRef = useRef<HTMLTextAreaElement>(null)
    const { likeProfilePostsHandler, updateConnectionPostsHandler, commentProfilePostsHandler, editProfilePostCommentHandler, deleteProfilePostCommentHandler } = usePostsHandlers()
    const { showSuccessToast, showErrorToast, setChooseTemplateModal, setDestructiveModal, hideModal, openSidebar } = useAppHandlers();
    const { store: { user } } = useAuthStore()
    const { messageClickHandler, getConnectionByPublicIdentifierHandler } = useConnectionsHandlers()
    const { visitLIProfile } = useExternalHandlers()
    const { storeTabId } = useExtensionRequest()

    const feedDashClientSignal = process.env.REACT_APP_FEED_DASH_CLIENT_SIGNAL

    // Comments sort options
    const commentsSortOptions = [
        { value: "newest", label: "Most recent" },
        { value: "oldest", label: "Oldest" },
        { value: "most_engagement", label: "Most engagement" },
        { value: "least_engagement", label: "Least engagement" },
    ]

    // Sort comments based on selected option
    const getSortedComments = useCallback(() => {
        if (!localComments || localComments.length === 0) return []

        const comments = [...localComments]

        let sortedComments
        switch (commentsSortBy.value) {
            case "newest":
                sortedComments = comments.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                break
            case "oldest":
                sortedComments = comments.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
                break
            case "most_engagement":
                // For now, sort by newest since we don't have engagement data in comments
                sortedComments = comments.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                break
            case "least_engagement":
                // For now, sort by oldest since we don't have engagement data in comments
                sortedComments = comments.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
                break
            default:
                sortedComments = comments
        }

        // Return only visible comments
        return sortedComments.slice(0, visibleCommentsCount)
    }, [localComments, commentsSortBy.value, visibleCommentsCount])

    const handleCommentsSortChange = (selectedOption: any) => {
        const newSort = {
            label: <Text $label>{selectedOption.label}</Text>,
            value: selectedOption.value,
        }
        setCommentsSortBy(newSort)
    }

    const handleShowMoreComments = () => {
        setVisibleCommentsCount(localComments.length)
    }

    const handleShowLessComments = () => {
        setVisibleCommentsCount(1)
    }

    const getRemainingCommentsCount = () => {
        return Math.max(0, localComments.length - visibleCommentsCount)
    }

    const handleToggleCommentExpansion = (commentId: string) => {
        setExpandedComments(prev => {
            const newSet = new Set(prev)
            if (newSet.has(commentId)) {
                newSet.delete(commentId)
            } else {
                newSet.add(commentId)
            }
            return newSet
        })
    }

    const isCommentExpanded = (commentId: string) => {
        return expandedComments.has(commentId)
    }

    const shouldTruncateComment = (text: string) => {
        // Check if text has more than 2 lines (approximately 100 characters per line)
        return text.length > 200
    }

    const extractCommentUrn = (entityUrn: string) => {
        // Extract commentUrn from entityUrn
        // From: urn:li:fsd_normComment:urn:li:fsd_comment:(...)
        // To: urn:li:fsd_comment:(...)
        if (entityUrn?.includes('urn:li:fsd_comment:')) {
            const parts = entityUrn.split('urn:li:fsd_comment:')
            return parts.length > 1 ? `urn:li:fsd_comment:${parts[1]}` : entityUrn
        }
        return entityUrn
    }

    const handleEditComment = (commentId: string) => {
        const comment = localComments.find(c => c._id === commentId || c.commentUrn === commentId)
        if (comment) {
            setEditingCommentId(commentId)
            setEditingCommentText(comment.text)
            // Focus on textarea after state update
            setTimeout(() => {
                if (editCommentRef.current) {
                    editCommentRef.current.focus()
                    autosizeTextarea(editCommentRef.current)
                }
            }, 0)
        }
    }

    const handleCancelEdit = () => {
        setEditingCommentId(null)
        setEditingCommentText('')
        setEditCommentSparkleLoading(false)
    }

    const handleSaveEdit = async () => {
        if (!editingCommentId || !editingCommentText.trim()) return

        const comment = localComments.find(c => c._id === editingCommentId || c.commentUrn === editingCommentId)
        if (!comment) return

        // Store original text for potential revert
        const originalText = comment.text

        try {
            const payload = {
                text: editingCommentText.trim(),
                commentUrn: comment.commentUrn
            }

            // Update local state optimistically
            setLocalComments(prev => prev.map(c =>
                (c._id === editingCommentId || c.commentUrn === editingCommentId)
                    ? { ...c, text: editingCommentText.trim() }
                    : c
            ))

            const linkedinResult = await editProfilePostCommentHandler(payload)

            console.log('linkedinResult', linkedinResult)
            console.log(linkedinResult)

            if (linkedinResult.status) {
                // Save comment update to database
                const commentPayload = {
                    postId: postData?.id,
                    connectionId: postData?.connectionId,
                    commentText: editingCommentText.trim(),
                    commentUrn: comment.commentUrn,
                    commentUpdated: true
                }

                await updateConnectionPostsHandler(commentPayload)

                setEditingCommentId(null)
                setEditingCommentText('')
                setEditCommentSparkleLoading(false)
                showSuccessToast({message: 'Comment updated successfully'})
            } else {
                // Revert optimistic update if LinkedIn call failed
                setLocalComments(prev => prev.map(c =>
                    (c._id === editingCommentId || c.commentUrn === editingCommentId)
                        ? { ...c, text: originalText }
                        : c
                ))
                showErrorToast({message: 'Failed to update comment. Please try again.'})
            }
        } catch (error) {
            console.error('Error editing comment:', error)
            // Revert optimistic update on error
            setLocalComments(prev => prev.map(c =>
                (c._id === editingCommentId || c.commentUrn === editingCommentId)
                    ? { ...c, text: originalText }
                    : c
            ))
            showErrorToast({message: 'Failed to update comment. Please try again.'})
        }
    }

    const handleDeleteComment = (commentId: string) => {
        setDestructiveModal({
            headingText: "Delete comment",
            descriptionText: "Are you sure you want to delete this comment?",
            onCancelHandler: () => {
                hideModal();
            },
            onConfirmHandler: () => {
                hideModal();
                performDeleteComment(commentId);
            },
        });
    }

    const performDeleteComment = async (commentId: string) => {
        const comment = localComments.find(c => c._id === commentId || c.commentUrn === commentId)
        if (!comment || !comment.commentUrn) return

        try {
            // Optimistically remove from UI
            setLocalComments(prev => prev.filter(c => c._id !== commentId && c.commentUrn !== commentId))

            // Call LinkedIn API to delete comment
            const linkedinResult = await deleteProfilePostCommentHandler(comment.commentUrn)

            if (linkedinResult.status) {
                // Update comment count
                onPostHandler && onPostHandler(postData?.id, 'num_comments', null, null, postData?.blob?.num_comments - 1)

                // Save to database
                const commentPayload = {
                    postId: postData?.id,
                    connectionId: postData?.connectionId,
                    commentUrn: comment.commentUrn,
                    commentDeleted: true,
                    num_comments: postData?.blob?.num_comments - 1
                }

                await updateConnectionPostsHandler(commentPayload)
                showSuccessToast({message: 'Comment deleted successfully'})
            } else {
                // Revert optimistic update if LinkedIn call failed
                setLocalComments(prev => [comment, ...prev])
                onPostHandler && onPostHandler(postData?.id, 'num_comments', null, null, postData?.blob?.num_comments)
                showErrorToast({message: 'Failed to delete comment. Please try again.'})
            }
        } catch (error) {
            console.error('Error deleting comment:', error)
            // Revert optimistic update on error
            setLocalComments(prev => [comment, ...prev])
            onPostHandler && onPostHandler(postData?.id, 'num_comments', null, null, postData?.blob?.num_comments)
            showErrorToast({message: 'Failed to delete comment. Please try again.'})
        }
    }

    // Create IConnection object from post data for use with ConnectionActionWithFeedsDropdown
    // Use the connection ID from post._id (this is the actual connection ID)
    // and feeds data from post.feeds (this contains the feed info for this connection)
    const connectionFeeds = useMemo(() => post.feeds || [], [post.feeds]);
    const [localFeeds, setLocalFeeds] = useState<{ _id: string; name: string; dateAdded?: string; }[]>(connectionFeeds)

    const connectionFromPost: IConnection = useMemo(() => ({
        _id: post._id, // Use connection ID from post, not profileInfo._id
        firstName: profileInfo.firstName,
        lastName: profileInfo.lastName,
        publicIdentifier: profileInfo.publicIdentifier,
        profilePicture: profileInfo.profilePicture,
        headline: profileInfo.headline,
        email: '', // Not available in profileInfo
        jobTitle: profileInfo.headline || '',
        name: `${profileInfo.firstName} ${profileInfo.lastName}`,
        phoneNumber: '',
        company: '',
        country: '',
        location: '',
        industry: '',
        profileId: profileInfo._id,
        connectedAt: 0,
        followerCount: 0,
        imported: false,
        notConnectedLinkedin: false,
        lastMessagedAtArray: [],
        notes: [],
        birthDate: null,
        languages: [],
        isHidden: false,
        users: [],
        isDisconnected: false,
        autoTagsArray: profileInfo.autoTagsArray,
        removedFromLD: false,
        kanbanColumns: [],
        p: [],
        tagsArray: [],
        enrichedPrivateEmails: [],
        enrichedBusinessEmails: [],
        enrichedPhones: [],
        feeds: localFeeds, // Use local feeds state
        isPostTracked: false,
        tags: [],
        map: () => [], // Required by IConnection interface
        profile: null // Required by IConnection interface
    }), [post._id, profileInfo, localFeeds])

    const enableLinkedinAction = isLoggedToLinkedin && extensionInstalled && connectionFromPost.publicIdentifier && extensionVersion === process.env.REACT_APP_VERSION
    const isLinkedinFirst = isLNFirst(connectionFromPost, user?._id);

    const openConnectionSidebar = useCallback(async () => {
        try {
            const publicIdentifier = connectionFromPost.publicIdentifier
            if (publicIdentifier) {
                const result = await getConnectionByPublicIdentifierHandler(publicIdentifier)
                const fullConnection = result?.data || result
                if (fullConnection?._id) {
                    openSidebar(SidebarTypes.CONNECTION_ACTIONS, { connection: fullConnection })
                    return
                }
            }
        } catch (e) {
            // fallback below
        }
        openSidebar(SidebarTypes.CONNECTION_ACTIONS, { connection: connectionFromPost })
    }, [connectionFromPost, getConnectionByPublicIdentifierHandler, openSidebar])

    const handleMouseEnter = () => {
        if (hoverTimeout) {
            clearTimeout(hoverTimeout)
            setHoverTimeout(null)
        }
        setShowHoverPopup(true)
    }

    const handleMouseLeave = () => {
        const timeout = setTimeout(() => {
            setShowHoverPopup(false)
        }, 200) // 200ms delay
        setHoverTimeout(timeout)
    }

    const handleReplyDropdownToggle = () => {
        setShowReplyDropdown(!showReplyDropdown)
    }

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowReplyDropdown(false)
            }
        }

        if (showReplyDropdown) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [showReplyDropdown])

    const handlePostLike = async (event: React.MouseEvent<HTMLDivElement> | null, reaction: string) => {
        event?.preventDefault()
        
        const originalLikedStatus = postData?.blob?.reacted

        const addReaction = originalLikedStatus === null && !!reaction
        const removeReaction = originalLikedStatus === reaction 
        const newReaction = originalLikedStatus !== null && originalLikedStatus !== reaction && !!reaction

        // Determine reaction action type
        let reactionAction: string
        if (addReaction) {
            reactionAction = 'add'
        } else if (removeReaction) {
            reactionAction = 'remove'
        } else if (newReaction) {
            reactionAction = 'new'
        } else {
            reactionAction = 'remove' // fallback
        }

        //This is a hardcoded query id for the posts feed, we should get it from the backend
        const socialDashReactions = {
            addReaction: process.env.REACT_APP_SOCIAL_DASH_ADD_REACTION as string,
            removeReaction: process.env.REACT_APP_SOCIAL_DASH_REMOVE_REACTION as string,
            newReaction: process.env.REACT_APP_SOCIAL_DASH_NEW_REACTION as string
        }

        // Choose the appropriate reaction ID based on the state
        let reactionId: string
        if (addReaction) {
            reactionId = socialDashReactions.addReaction
        } else if (removeReaction) {
            reactionId = socialDashReactions.removeReaction
        } else if (newReaction) {
            reactionId = socialDashReactions.newReaction
        } else {
            reactionId = socialDashReactions.removeReaction // fallback
        }

        const numLikes = postData?.blob?.num_likes + (addReaction ? 1 : removeReaction ? -1 : newReaction ? 0 : 0)

        onPostHandler && onPostHandler(postData?.id, 'reacted', (addReaction || newReaction) ? reaction : null, numLikes, null)

        const payload = {
            postId: postData?.id,
            connectionId: postData?.connectionId,
            reacted: (addReaction || newReaction) ? reaction : null,
            num_likes: numLikes
        }

        try {
            await updateConnectionPostsHandler(payload)

            const linkedinPayload = {
                reactionAction: reactionAction,
                postId: postData?.id,
                ugcPostId: postData?.blob?.ugcPostId,
                reactionType: addReaction ? reaction : (removeReaction || newReaction) && originalLikedStatus,
                newReactionType: reaction,
                graphqlQueryId: reactionId,
                feedSignalQueryId: feedDashClientSignal
            }

            const linkedinResult = await likeProfilePostsHandler(linkedinPayload)
            
            if (!linkedinResult && onPostHandler) {
                onPostHandler(postData?.id, 'reacted', originalLikedStatus, numLikes, null)
            }

            
        } catch (error) {
            console.error('Error liking post:', error)
            
            if (onPostHandler) {
                onPostHandler(postData?.id, 'reacted', originalLikedStatus, numLikes, null)
            }
        }
    }

    const handleSkipPost = async () => {
        if (!postData?.id) return;

        // Optimistically remove from UI
        onPostHandler && onPostHandler(postData.id, 'skipped', null, null, null);

        const payload = {
            postId: postData.id,
            connectionId: postData.connectionId,
            skipped: true
        };

        try {
            await updateConnectionPostsHandler(payload);
        } catch (error) {
            console.error('Error skipping post:', error);
            // Note: We don't revert here since the post is already removed from UI
            // In a real app, you might want to add it back to the list
        }
    };

    const handleLikeAndSkipPost = async () => {
        if (!postData?.id) return;

        // First like the post
        await handlePostLike(null, 'LIKE');
        
        // Then skip it (but don't call handleSkipPost as it would send another API call)
        // Just remove from UI and send skip payload
        onPostHandler && onPostHandler(postData.id, 'skipped', null, null, null);
        
        const payload = {
            postId: postData.id,
            connectionId: postData.connectionId,
            skipped: true
        };

        try {
            await updateConnectionPostsHandler(payload);
        } catch (error) {
            console.error('Error skipping post:', error);
        }
    };

    const handleCommentProfilePosts = async (likePost: boolean) => {
        setShowReplyDropdown(false)

        // Store comment text before clearing it
        const commentToSave = commentText.trim()

        // Clear comment input
        setCommentText('')

        // Validate comment text
        if (!commentToSave) {
            showErrorToast({message: 'Please enter a comment'})
            return
        }

        // Optimistically update UI
        onPostHandler && onPostHandler(postData?.id, 'num_comments', null, null, postData?.blob?.num_comments + 1)

        // Create optimistic comment object
        const optimisticComment = {
            text: commentToSave,
            user: {
                _id: user?._id || "",
                firstName: user?.firstName || "",
                lastName: user?.lastName || "",
                occupation: user?.linkedinData?.occupation || "",
                profilePicture: user?.linkedinData?.profilePicture || null
            },
            createdAt: new Date().toISOString(),
            commentUrn: undefined // Will be updated after successful API call
        }

        // Add comment optimistically to local state
        setLocalComments(prev => [optimisticComment, ...prev])

        // Like post if requested
        if (likePost) await handlePostLike(null, 'LIKE')

        try {
            // Send comment to LinkedIn
            const linkedinPayload = {
                postId: postData?.id,
                ugcPostId: postData?.blob?.ugcPostId,
                text: commentToSave,
                reactionType: 'submitComment',
                feedSignalQueryId: feedDashClientSignal
            }

           const linkedinResult = await commentProfilePostsHandler(linkedinPayload)

           console.log('linkedinResult', linkedinResult)
           console.log(linkedinResult)

            if (linkedinResult.status) {
                // Extract commentUrn from entityUrn
                const entityUrn = linkedinResult.parsed?.data?.entityUrn
                const commentUrn = extractCommentUrn(entityUrn)

                // Save comment to database
                const commentPayload = {
                    postId: postData?.id,
                    connectionId: postData?.connectionId,
                    commentText: commentToSave,
                    commentUrn: commentUrn,
                    num_comments: postData?.blob?.num_comments + 1,
                }

                await updateConnectionPostsHandler(commentPayload)

                // Update optimistic comment with real commentUrn
                setLocalComments(prev => prev.map(c =>
                    c.text === commentToSave && c.commentUrn === undefined
                        ? { ...c, commentUrn: commentUrn }
                        : c
                ))

                showSuccessToast({message: 'Comment added successfully'})
            } else {
                // Revert optimistic update if LinkedIn call failed
                onPostHandler && onPostHandler(postData?.id, 'num_comments', null, null, postData?.blob?.num_comments)
                showErrorToast({message: 'Failed to add comment. Please try again.'})
            }
        } catch (error) {
            console.error('Error adding comment:', error)
            // Revert optimistic update on error
            onPostHandler && onPostHandler(postData?.id, 'num_comments', null, null, postData?.blob?.num_comments)
            // Remove optimistic comment from local state
            setLocalComments(prev => prev.filter(comment => comment.user._id !== (user?._id || "") || comment.text !== commentToSave))
            showErrorToast({message: 'Failed to add comment. Please try again.'})
        }
    }
    

    // Update localFeeds when connectionFeeds changes
    useEffect(() => {
        setLocalFeeds(connectionFeeds);
    }, [connectionFeeds]);

    // Initialize local comments when postData changes
    useEffect(() => {
        if (postData?.userComments) {
            setLocalComments(postData.userComments);
        }
    }, [postData?.userComments]);

    // Check if content height exceeds 400px
    useEffect(() => {
        if (contentRef.current) {
            const height = contentRef.current.scrollHeight;
            setShowExpandButton(height > 400);
        }
    }, [postData?.blob?.post_content]);


    const toggleExpanded = useCallback(() => {
        setIsExpanded(!isExpanded);
    }, [isExpanded]);

    const nextSlide = useCallback(() => {
        setCurrentSlide(prev => (prev + 1) % (pdfPages.length || 4));
    }, [pdfPages.length]);

    const prevSlide = useCallback(() => {
        setCurrentSlide(prev => (prev - 1 + (pdfPages.length || 4)) % (pdfPages.length || 4));
    }, [pdfPages.length]);

    const toggleFullscreen = useCallback(() => {
        setIsFullscreen(!isFullscreen);
    }, [isFullscreen]);

    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    }, []);

    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX);
    }, []);

    const handleTouchEnd = useCallback(() => {
        if (!touchStart || !touchEnd) return;
        
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe) {
            nextSlide();
        } else if (isRightSwipe) {
            prevSlide();
        }
    }, [touchStart, touchEnd, nextSlide, prevSlide]);

    // Load PDF and render pages
    const loadPDF = useCallback(async () => {
        if (!postData?.blob?.post_document || postData?.post_type !== 'document') return;
        
        console.log('Loading PDF:', postData.blob.post_document);
        setIsPdfLoading(true);
        try {
            // Configure PDF.js worker - use local worker file
            pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
            
            // Load PDF document with fallback options
            const loadingTask = pdfjsLib.getDocument({
                url: postData.blob.post_document,
                cMapUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/cmaps/`,
                cMapPacked: true,
                standardFontDataUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/standard_fonts/`
            });
            
            const pdf = await loadingTask.promise;
            const totalPages = pdf.numPages;
            console.log('PDF loaded, total pages:', totalPages);
            
            // Initialize canvas refs array
            canvasRefs.current = new Array(Math.min(totalPages, 4)).fill(null);
            
            // Set PDF pages first so DOM elements are rendered
            const pages = Array.from({ length: Math.min(totalPages, 4) }, (_, i) => `page-${i + 1}`);
            setPdfPages(pages);
            
            // Wait a bit for DOM to render, then render pages
            setTimeout(() => {
                // Render each page to canvas
                const pagePromises = [];
                for (let pageNum = 1; pageNum <= Math.min(totalPages, 4); pageNum++) {
                    pagePromises.push(
                        pdf.getPage(pageNum).then(async (page) => {
                            console.log(`Rendering page ${pageNum}`);
                            const canvas = canvasRefs.current[pageNum - 1];
                            if (!canvas) {
                                console.error(`Canvas not found for page ${pageNum}`);
                                return;
                            }
                        
                        const context = canvas.getContext('2d');
                        if (!context) {
                            console.error(`Context not found for page ${pageNum}`);
                            return;
                        }
                        
                        const viewport = page.getViewport({ scale: 1.5 });
                        canvas.height = viewport.height;
                        canvas.width = viewport.width;
                        
                        const renderContext = {
                            canvasContext: context,
                            viewport: viewport,
                            canvas: canvas
                        };
                        
                            await page.render(renderContext).promise;
                            console.log(`Page ${pageNum} rendered successfully`);
                            
                            // Mark canvas as having content
                            canvas.setAttribute('data-has-content', 'true');
                        })
                    );
                }
                
                Promise.all(pagePromises).then(() => {
                    console.log('All pages rendered successfully');
                });
            }, 100); // Wait 100ms for DOM to render
        } catch (error) {
            console.error('Error loading PDF:', error);
            // Set fallback pages for demonstration
            setPdfPages(['page-1', 'page-2', 'page-3', 'page-4']);
            // Initialize canvas refs for fallback
            canvasRefs.current = new Array(4).fill(null);
            
            // Try iframe fallback as last resort
            console.log('Trying iframe fallback...');
        } finally {
            setIsPdfLoading(false);
        }
    }, [postData?.blob?.post_document, postData?.post_type]);

    const onLinkedinActionHandler = async (e: React.MouseEvent<HTMLElement>) => {
        const publicIdentifier = connectionFromPost.publicIdentifier
            
        if (publicIdentifier) {
            if(!isLinkedinFirst) {
                storeTabId('storeAppTabId')
                visitLIProfile(publicIdentifier)
            } else {
                setLoadingConversation(connectionFromPost?._id)
                const result = await getConnectionByPublicIdentifierHandler(publicIdentifier)
                const fullConnection = result?.data || result
                if(fullConnection?._id) await messageClickHandler(e, fullConnection)
                setLoadingConversation(null)
            }
        }
    }

    // Load PDF when component mounts or PDF URL changes
    useEffect(() => {
        if (postData?.post_type === 'document' && postData?.blob?.post_document) {
            loadPDF();
        }
    }, [postData?.post_type, postData?.blob?.post_document, loadPDF]);

    // Draw fallback content on canvas if PDF fails to load
    useEffect(() => {
        if (pdfPages.length > 0 && !isPdfLoading) {
            // Check if any canvas has content, if not draw fallback
            const hasContent = canvasRefs.current.some(canvas => {
                if (!canvas) return false;
                const ctx = canvas.getContext('2d');
                if (!ctx) return false;
                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                return imageData.data.some(pixel => pixel !== 0);
            });

            if (!hasContent) {
                // Draw fallback content on all canvases
                canvasRefs.current.forEach((canvas, index) => {
                    if (!canvas) return;
                    const ctx = canvas.getContext('2d');
                    if (!ctx) return;

                    // Set canvas size
                    canvas.width = 600;
                    canvas.height = 800;

                    // Clear canvas
                    ctx.clearRect(0, 0, canvas.width, canvas.height);

                    // Draw background
                    ctx.fillStyle = '#ffffff';
                    ctx.fillRect(0, 0, canvas.width, canvas.height);

                    // Draw border
                    ctx.strokeStyle = '#e0e0e0';
                    ctx.lineWidth = 2;
                    ctx.strokeRect(0, 0, canvas.width, canvas.height);

                    // Draw title
                    ctx.fillStyle = '#333333';
                    ctx.font = 'bold 24px Arial';
                    ctx.textAlign = 'center';
                    ctx.fillText('PDF Document', canvas.width / 2, 100);

                    // Draw page number
                    ctx.font = '16px Arial';
                    ctx.fillText(`Page ${index + 1}`, canvas.width / 2, 150);

                    // Draw content
                    ctx.font = '14px Arial';
                    ctx.fillText('This PDF could not be loaded', canvas.width / 2, 250);
                    ctx.fillText('Click the download button to view', canvas.width / 2, 280);
                    ctx.fillText('the original document', canvas.width / 2, 310);

                    // Draw icon
                    ctx.font = '48px Arial';
                    ctx.fillText('📄', canvas.width / 2, 400);
                    
                    // Mark canvas as having content
                    canvas.setAttribute('data-has-content', 'true');
                });
            }
        }
    }, [pdfPages, isPdfLoading]);

    // Keyboard navigation for carousel
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (postData?.post_type === 'document' && postData?.blob?.post_document) {
                if (event.key === 'ArrowLeft') {
                    event.preventDefault();
                    prevSlide();
                } else if (event.key === 'ArrowRight') {
                    event.preventDefault();
                    nextSlide();
                } else if (event.key === 'Escape' && isFullscreen) {
                    event.preventDefault();
                    setIsFullscreen(false);
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [postData?.post_type, postData?.blob?.post_document, prevSlide, nextSlide, isFullscreen]);

    const replaceProfilePlaceholders = (text: string): string => replacePlaceholders(text, {
        firstName: profileInfo?.firstName || '',
        lastName: profileInfo?.lastName || '',
        senderName: `${profileInfo?.firstName || ''} ${profileInfo?.lastName || ''}`.trim(),
        company: ''
    })

    // Function to highlight search terms in HTML content without breaking existing tags
    const highlightSearchTerms = useMemo(() => {
        if (!searchValues || searchValues.length === 0) {
            return (html: string) => html;
        }

        return (html: string): string => {
            let highlightedHtml = html;

            // Process each search term sequentially
            searchValues.forEach((searchObj, index) => {
                const searchTerm = searchObj.searchValue;
                if (!searchTerm || searchTerm.trim() === '') return;

                // Escape special regex characters in the search term
                const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                
                // Helper function to parse HTML and highlight only text nodes
                const parseAndHighlight = (htmlContent: string): string => {
                    const parts: Array<{ type: 'tag' | 'text'; content: string }> = [];
                    let currentIndex = 0;
                    
                    // Parse HTML into tags and text
                    while (currentIndex < htmlContent.length) {
                        const tagStart = htmlContent.indexOf('<', currentIndex);
                        
                        if (tagStart === -1) {
                            if (currentIndex < htmlContent.length) {
                                parts.push({ type: 'text', content: htmlContent.substring(currentIndex) });
                            }
                            break;
                        }
                        
                        if (tagStart > currentIndex) {
                            parts.push({ type: 'text', content: htmlContent.substring(currentIndex, tagStart) });
                        }
                        
                        const tagEnd = htmlContent.indexOf('>', tagStart);
                        if (tagEnd === -1) {
                            parts.push({ type: 'text', content: htmlContent.substring(tagStart) });
                            break;
                        }
                        
                        parts.push({ type: 'tag', content: htmlContent.substring(tagStart, tagEnd + 1) });
                        currentIndex = tagEnd + 1;
                    }

                    // Highlight search terms in text nodes only
                    const regex = new RegExp(`(${escapedTerm})`, 'gi');
                    parts.forEach((part) => {
                        if (part.type === 'text' && !part.content.includes('search-highlight')) {
                            // Only highlight text that's not already inside a highlight span
                            part.content = part.content.replace(regex, (match) => {
                                return `<span class="search-highlight-${index}" style="background-color: #FCD98D;">${match}</span>`;
                            });
                        }
                    });

                    return parts.map(p => p.content).join('');
                };

                highlightedHtml = parseAndHighlight(highlightedHtml);
            });

            return highlightedHtml;
        };
    }, [searchValues]);

    // Compute processed post content with highlighting
    const processedPostContent = useMemo(() => {
        const rawContent = postData?.blob?.post_content || '';
        
        // First, apply hashtag and URL linkification
        let processed = rawContent
            // 1) Linkify hashtags first so we don't match hex colors inside added anchors later
            .replace(
                /#(\w+)/g,
                (match: string, hashtag: string) => {
                    const linkedinUrl = `https://www.linkedin.com/search/results/all/?keywords=%23${hashtag.toLowerCase()}&origin=HASH_TAG_FROM_FEED&sid=E!S`;
                    return `<a href="${linkedinUrl}" target="_blank" rel="noopener noreferrer" style="color: ${styles.colors.primary600}; text-decoration: none; cursor: pointer;" onmouseover="this.style.textDecoration='underline'" onmouseout="this.style.textDecoration='none'">${match}</a>`;
                }
            )
            // 2) Then linkify any URL starting with http/https or www.
            .replace(/(^|[\s([{<@])((?:https?:\/\/|www\.)\S+?)([)\]}>,.!?:;]*)(?=$|\s)/gi, (m: string, pre: string, url: string, trailing: string) => {
                const href = url.startsWith('http') ? url : `https://${url}`;
                return `${pre}<a href="${href}" target="_blank" rel="noopener noreferrer" style="color: ${styles.colors.primary600}; text-decoration: none; cursor: pointer;" onmouseover="this.style.textDecoration='underline'" onmouseout="this.style.textDecoration='none'">${url}</a>${trailing}`;
            });

        // Then apply search term highlighting
        processed = highlightSearchTerms(processed);

        return processed;
    }, [postData?.blob?.post_content, highlightSearchTerms]);

    return (
        <Container>
            <HeaderSection>
                <Row gap='12px' alignItems style={{ maxWidth: '90%' }}>
                    <ProfilePhoto 
                        source={profileInfo?.profilePicture} 
                        capitalLetters={`${profileInfo?.firstName?.charAt(0)?.toUpperCase() || ''}${profileInfo?.lastName?.charAt(0)?.toUpperCase() || ''}`}
                        tooltipMessage={`${profileInfo?.firstName} ${profileInfo?.lastName}`}
                        minWidth='48px'
                        minHeight='48px'
                        width='48px'
                        height='48px'
                    />
                    <Column gap='2px' style={{ minWidth: 0, overflow: 'hidden' }}>
                        <Row gap='12px'>
                            <ActionButton width={'fit-content'} fontSize='11px' fontWeight='bold' onClick={openConnectionSidebar}>
                                {profileInfo?.firstName?.toUpperCase()} {profileInfo?.lastName?.toUpperCase()}
                            </ActionButton>
                            <TooltipWrapper tooltipText={!isLinkedinFirst ? 'Open LinkedIn profile to connect' : 'Send a message'}>
                                <ActionButton linkAction onClick={(e) => onLinkedinActionHandler(e)}>
                                    {loadingConversation === connectionFromPost?._id ? <LoadingSpinner color={styles.colors.primary600}/> : !isLinkedinFirst ? 'Connect' : 'Message'}
                                </ActionButton>
                            </TooltipWrapper>
                        </Row>
                        <HeadlineText>
                            <HeadlineContent>
                                {profileInfo?.headline && (
                                    <TooltipWrapper tooltipText={profileInfo.headline}>
                                        <HeadlineOccupationText>
                                            {profileInfo.headline}
                                        </HeadlineOccupationText>
                                    </TooltipWrapper>
                                )}
                                {profileInfo?.headline && postData?.time_ago_text && postData?.time_ago && (
                                    <span style={{ flexShrink: 0 }}> &nbsp;•&nbsp; </span>
                                )}
                                {postData?.time_ago_text && postData?.time_ago && (
                                    <HeadlineDateText>
                                        {formatTimeAgo(postData.time_ago_text, postData.time_ago)}
                                    </HeadlineDateText>
                                )}
                            </HeadlineContent>
                        </HeadlineText>
                    </Column>
                </Row>
                <ConnectionActionWithFeedsDropdown
                    connection={connectionFromPost}
                    currentFeedId={currentFeedId}
                    onUpdate={(updatedFeeds) => {
                        // Update local feeds state
                        if (updatedFeeds) {
                            setLocalFeeds(updatedFeeds);
                        }
                        // Call the parent's feed update handler if provided
                        if (onFeedUpdate) {
                            const updatedConnection: IConnection = {
                                ...connectionFromPost,
                                feeds: updatedFeeds || localFeeds
                            };
                            onFeedUpdate(updatedConnection);
                        }
                    }}
                    onCreateFeed={onCreateFeed}
                    className="post-card-actions-dropdown"
                />
            </HeaderSection>
            
            <PostContent>
                <ExpandableTextContainer $isExpanded={isExpanded} $showExpandButton={showExpandButton}>
                    <PostTextWithHTML
                        ref={contentRef}
                        dangerouslySetInnerHTML={{
                            __html: processedPostContent
                        }}
                    />
                    {!isExpanded && showExpandButton && <FadeOverlay />}
                </ExpandableTextContainer>
                {showExpandButton && (
                    <ShowMoreButton onClick={toggleExpanded}>
                        {isExpanded ? (
                            <>
                                <ShowMoreText>Show less</ShowMoreText>
                                <RotatedArrow>
                                    <SvgFragment type="arrowDown" />
                                </RotatedArrow>
                            </>
                        ) : (
                            <>
                                <ShowMoreText>Show more</ShowMoreText>
                                <SvgFragment type="arrowDown" />
                            </>
                        )}
                    </ShowMoreButton>
                )}
                {postData?.post_sub_type === 'reshare' && postData?.reposted_post && (
                    <RepostContainer>
                        {post.profileInfoReposted && (
                            <RepostIndicator>
                                <ProfilePhoto 
                                    source={post.profileInfoReposted?.profilePicture} 
                                    capitalLetters={`${post.profileInfoReposted?.firstName?.charAt(0)?.toUpperCase() || ''}${post.profileInfoReposted?.lastName?.charAt(0)?.toUpperCase() || ''}`}
                                    tooltipMessage={`${post.profileInfoReposted?.firstName} ${post.profileInfoReposted?.lastName}`}
                                    minWidth='48px'
                                    minHeight='48px'
                                    width='48px'
                                    height='48px'
                                />
                                <Column gap='2px'>
                                    <Text $heading6 $bold>
                                        {post.profileInfoReposted?.firstName?.toUpperCase()} {post.profileInfoReposted?.lastName?.toUpperCase()}
                                    </Text>
                                    <Row gap='8px' alignItems>
                                         <HeadlineText>
                                             {post.profileInfoReposted?.headline}
                                             {postData?.reposted_post?.time_ago_text && postData?.reposted_post?.time_ago && (
                                                 <>
                                                     {' • '}
                                                     {formatTimeAgo(postData.reposted_post.time_ago_text, postData.reposted_post.time_ago)}
                                                 </>
                                             )}
                                         </HeadlineText>
                                    </Row>
                                </Column>
                            </RepostIndicator>
                        )}
                        {!post.profileInfoReposted && postData?.linked_in_company_name && (
                            <RepostIndicator>
                                <ProfilePhoto 
                                    source={postData.linked_in_company_logo}
                                    capitalLetters={postData.linked_in_company_name.charAt(0).toUpperCase()}
                                    tooltipMessage={postData.linked_in_company_name}
                                    minWidth='48px'
                                    minHeight='48px'
                                    width='48px'
                                    height='48px'
                                />
                                <Column gap='2px'>
                                    <Text $heading6 $bold>
                                        {postData.linked_in_company_name.toUpperCase()}
                                    </Text>
                                    <Row gap='8px' alignItems>
                                         <HeadlineText>
                                             {postData?.reposted_post?.time_ago_text && postData?.reposted_post?.time_ago && (
                                                 formatTimeAgo(postData.reposted_post.time_ago_text, postData.reposted_post.time_ago)
                                             )}
                                         </HeadlineText>
                                    </Row>
                                </Column>
                            </RepostIndicator>
                        )}
                        <RepostedContent>
                            <RepostedTextWithHTML
                                dangerouslySetInnerHTML={{
                                    __html: postData.reposted_post.blob?.post_content?.replace(
                                        /#(\w+)/g,
                                        (match: string, hashtag: string) => {
                                            const linkedinUrl = `https://www.linkedin.com/search/results/all/?keywords=%23${hashtag.toLowerCase()}&origin=HASH_TAG_FROM_FEED&sid=E!S`;
                                            return `<a href="${linkedinUrl}" target="_blank" rel="noopener noreferrer" style="color: ${styles.colors.primary600}; text-decoration: none; cursor: pointer;" onmouseover="this.style.textDecoration='underline'" onmouseout="this.style.textDecoration='none'">${match}</a>`;
                                        }
                                    ) || ''
                                }}
                            />
                            {postData.reposted_post.post_type === 'image' && postData.reposted_post.blob?.post_image && (
                                <RepostedImage 
                                    src={postData.reposted_post.blob.post_image} 
                                    alt="Reposted post image"
                                    onClick={() => postData.reposted_post.blob.post_image && window.open(postData.reposted_post.blob.post_image, '_blank')}
                                    onError={(e) => {
                                        e.currentTarget.style.display = 'none'
                                    }}
                                />
                            )}
                            {postData.reposted_post.post_type === 'video' && postData.reposted_post.blob?.post_video && (
                                <RepostedVideo 
                                    src={postData.reposted_post.blob.post_video}
                                    controls
                                    preload="metadata"
                                    poster=""
                                    onError={(e) => {
                                        console.error('Error loading reposted video:', e)
                                        e.currentTarget.style.display = 'none'
                                    }}
                                />
                            )}
                            {postData.reposted_post.post_type === 'document' && postData.reposted_post.blob?.post_document && (
                                <RepostedPdfContainer>
                                    <RepostedPdfIframe
                                        src={`${postData.reposted_post.blob.post_document}#toolbar=1&navpanes=1&scrollbar=1`}
                                        title="Reposted PDF Document"
                                        onLoad={() => console.log('Reposted PDF loaded successfully')}
                                        onError={() => console.error('Reposted PDF load error')}
                                    />
                                </RepostedPdfContainer>
                            )}
                            {postData.reposted_post.post_type === 'article' && (postData.reposted_post.blob?.article || postData.reposted_post.blob?.post_article) && (
                                <RepostedArticleContainer>
                                    <RepostedArticleImage>
                                        {postData.reposted_post.blob?.article?.images?.[0]?.url
                                            ? <RepostedArticleThumb 
                                                src={postData.reposted_post.blob.article.images[0].url} 
                                                alt={postData.reposted_post.blob.article?.accessibilityText || 'Article image'}
                                                onError={(e: React.SyntheticEvent<HTMLImageElement>) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
                                              />
                                            : <RepostedArticleImagePlaceholder>📰</RepostedArticleImagePlaceholder>
                                        }
                                    </RepostedArticleImage>
                                    <RepostedArticleContent>
                                        <RepostedArticleTitle>
                                            <a 
                                                href={postData.reposted_post.blob?.article?.actionTarget || postData.reposted_post.blob.permalink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: styles.colors.black600, textDecoration: 'none' }}
                                            >
                                                {postData.reposted_post.blob?.article?.accessibilityText || postData.reposted_post.blob.post_content.split('\n')[0] || 'Article'}
                                            </a>
                                        </RepostedArticleTitle>
                                        <RepostedArticleDescription>
                                            {postData.reposted_post.blob.post_content.split('\n').slice(1).join(' ').substring(0, 120)}...
                                        </RepostedArticleDescription>
                                        <RepostedArticleSource>
                                            {(() => { try { return new URL(postData.reposted_post.blob?.article?.actionTarget || '').hostname.replace('www.', '') } catch { return 'linkedin.com' } })()}
                                        </RepostedArticleSource>
                                    </RepostedArticleContent>
                                </RepostedArticleContainer>
                            )}
                            {postData.reposted_post.post_type === 'poll' && postData.reposted_post.blob?.post_poll && (
                                <RepostedPollContainer>
                                    <PollDisplay pollData={postData.reposted_post.blob.post_poll} />
                                </RepostedPollContainer>
                            )}
                        </RepostedContent>
                    </RepostContainer>
                )}
                {postData?.post_type === 'image' && postData?.blob?.post_image && (
                    <PostImage 
                        src={postData.blob.post_image} 
                        alt="Post image"
                        onClick={() => postData.blob.post_image && window.open(postData.blob.post_image, '_blank')}
                        onError={(e) => {
                            e.currentTarget.style.display = 'none'
                        }}
                    />
                )}
                
                {postData?.post_type === 'image_carousel' && postData?.blob?.post_images && (
                    <ImageCarousel images={postData.blob.post_images} />
                )}
                {postData?.post_type === 'video' && postData?.blob?.post_video && (
                    <PostVideo 
                        src={postData.blob.post_video}
                        controls
                        preload="metadata"
                        poster=""
                        onError={(e) => {
                            console.error('Error loading video:', e)
                            e.currentTarget.style.display = 'none'
                        }}
                    />
                )}
                {postData?.post_type === 'document' && postData?.blob?.post_document && (
                    <DocumentCarouselContainer 
                        $isFullscreen={isFullscreen}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                    >
                        <CarouselHeader $isFullscreen={isFullscreen}>
                            <SlideCounter $isFullscreen={isFullscreen}>
                                {currentSlide + 1} / {pdfPages.length || 4}
                            </SlideCounter>
                            <CarouselControls>
                            <TooltipWrapper tooltipText="Download">
                                <ControlButton $isFullscreen={isFullscreen} onClick={() => window.open(postData?.blob?.post_document, '_blank')}>
                                    <SvgFragment type="import" />
                                </ControlButton>
                            </TooltipWrapper>
                            <TooltipWrapper tooltipText="Fullscreen">
                                <ControlButton $isFullscreen={isFullscreen} onClick={toggleFullscreen}>
                                    <SvgFragment type="expand" />
                                </ControlButton>
                            </TooltipWrapper>
                            </CarouselControls>
                        </CarouselHeader>
                        
                        <CarouselContent $isFullscreen={isFullscreen}>
                            <NavigationButton $position="left" $isFullscreen={isFullscreen} onClick={prevSlide}>
                                <SvgFragment type="arrowLeft" />
                            </NavigationButton>
                            
                            <SlidesContainer $isFullscreen={isFullscreen}>
                                {isPdfLoading ? (
                                    <PDFLoadingContainer>
                                        <LoadingSpinner />
                                        <Text>Loading Document...</Text>
                                    </PDFLoadingContainer>
                                ) : pdfPages.length > 0 ? (
                                    <SlidesWrapper $currentSlide={currentSlide} $isFullscreen={isFullscreen}>
                                        {pdfPages.map((pageId, index) => (
                                            <DocumentSlide key={pageId} $isFullscreen={isFullscreen}>
                                                <PDFCanvas
                                                    ref={(el) => {
                                                        canvasRefs.current[index] = el;
                                                    }}
                                                    width={600}
                                                    height={800}
                                                    $isFullscreen={isFullscreen}
                                                />
                                                {/* Iframe fallback for PDF viewing */}
                                                <PDFIframeFallback
                                                    src={`${postData.blob.post_document}#page=${index + 1}&toolbar=0&navpanes=0&scrollbar=0&statusbar=0&messages=0&view=FitH&pagemode=none&zoom=100`}
                                                    title={`PDF Page ${index + 1}`}
                                                />
                                            </DocumentSlide>
                                        ))}
                                    </SlidesWrapper>
                                ) : (
                                    <PDFErrorContainer>
                                        <PDFErrorText>
                                            Document cannot be loaded. Please try again or open the document directly.
                                        </PDFErrorText>
                                        <PDFDownloadButton 
                                    href={postData.blob.post_document}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                        >
                                            📥 Open Document
                                        </PDFDownloadButton>
                                    </PDFErrorContainer>
                                )}
                            </SlidesContainer>
                            
                            <NavigationButton $position="right" $isFullscreen={isFullscreen} onClick={nextSlide}>
                                <SvgFragment type="arrowRight" />
                            </NavigationButton>
                        </CarouselContent>
                        
                        <CarouselFooter $isFullscreen={isFullscreen}>
                            <ProgressBar $isFullscreen={isFullscreen}>
                                <ProgressFill $progress={((currentSlide + 1) / (pdfPages.length || 4)) * 100} />
                            </ProgressBar>
                            <SwipeHint $isFullscreen={isFullscreen}>
                                Swipe ←
                            </SwipeHint>
                        </CarouselFooter>
                    </DocumentCarouselContainer>
                )}
                {postData?.post_type === 'article' && (postData?.blob?.article || postData?.blob?.post_article) && (
                    <PostArticleContainer>
                        <PostArticleImage>
                            {postData.blob?.article?.images?.[0]?.url
                                ? <PostArticleThumb 
                                    src={postData.blob.article.images[0].url} 
                                    alt={postData.blob.article?.accessibilityText || 'Article image'}
                                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
                                  />
                                : <PostArticleImagePlaceholder>📰</PostArticleImagePlaceholder>
                            }
                        </PostArticleImage>
                        <PostArticleContent>
                            <PostArticleTitle>
                                <a 
                                    href={postData.blob?.article?.actionTarget || postData.blob.permalink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: styles.colors.black600, textDecoration: 'none' }}
                                >
                                    {postData.blob?.article?.accessibilityText || postData.blob.post_content.split('\n')[0] || 'Article'}
                                </a>
                            </PostArticleTitle>
                            <PostArticleDescription>
                                {postData.blob.post_content.split('\n').slice(1).join(' ').substring(0, 120)}...
                            </PostArticleDescription>
                            <PostArticleSource>
                                <a 
                                    href={postData.blob?.article?.actionTarget || postData.blob.permalink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: styles.colors.blue600, textDecoration: 'none' }}
                                >
                                    {(() => { try { return new URL(postData.blob?.article?.actionTarget || '').hostname.replace('www.', '') } catch { return 'linkedin.com' } })()}
                                </a>
                            </PostArticleSource>
                        </PostArticleContent>
                        
                    </PostArticleContainer>
                )}
                {postData?.post_type === 'poll' && postData?.blob?.post_poll && (
                    <PollDisplay pollData={postData.blob.post_poll} />
                )}
                {postData?.post_type === 'celebration' && postData?.blob?.celebration && (
                    <CelebrationContainer>
                        <CelebrationImage 
                            src={postData.blob.celebration.image} 
                            alt="Celebration"
                            onError={(e) => {
                                e.currentTarget.style.display = 'none'
                            }}
                        />
                        <CelebrationHeadline>
                            {postData.blob.celebration.headline}
                        </CelebrationHeadline>
                    </CelebrationContainer>
                )}
            </PostContent>

            <PostStats>
                    <StatText className='like-stat'>
                            <SvgFragment type="emptyLike" />
                        {postData?.blob?.num_likes || 0}
                    </StatText>
                    <StatText>
                        <div style={{ cursor: 'pointer' }} onClick={() => console.log('comment post', postData)}>💬</div>
                        {postData?.blob?.num_comments || 0}
                    </StatText>
                    <StatText>
                        {postData?.blob?.num_comments || 0} comments
                    </StatText>
            </PostStats>

            <ActionButtonsSection>
                <TooltipWrapper tooltipText={!enableLinkedinAction ? "You need to be logged in to LinkedIn and have the extension installed to like posts" : ''}> 
                    <div> {/* pointer events preventing tooltip from being shown */}
                        <ActionButton 
                            disabled={!enableLinkedinAction} 
                            onMouseEnter={!enableLinkedinAction ? undefined : handleMouseEnter}
                            onMouseLeave={!enableLinkedinAction ? undefined : handleMouseLeave}
                            reacted={postData?.blob?.reacted} 
                            onClick={(e: any) => handlePostLike(e, postData?.blob?.reacted ?? 'LIKE')} 
                            className='like-action-button'>
                                <SvgFragment type={postData?.blob?.reacted ? postData?.blob?.reacted.toLowerCase() : 'emptyLike'} />
                                <PostActionText>{postData?.blob?.reacted ? (() => {
                                    const reactionMap: { [key: string]: string } = {
                                        'LIKE': 'Like',
                                        'PRAISE': 'Celebrate',
                                        'APPRECIATION': 'Support',
                                        'EMPATHY': 'Love',
                                        'INTEREST': 'Insightful',
                                        'ENTERTAINMENT': 'Funny'
                                    };
                                    return reactionMap[postData?.blob?.reacted];
                                })() : 'Like'}
                                </PostActionText>
                        </ActionButton>
                    </div>
                </TooltipWrapper>
                <ActionButton onClick={() => postData?.blob?.permalink && window.open(postData.blob.permalink, '_blank')}>
                    <SvgFragment type="open" />
                    <PostActionText>Open</PostActionText>
                </ActionButton>
                <ActionButton onClick={handleSkipPost}>
                    <SvgFragment type="skip" />
                    <PostActionText>Skip</PostActionText>
                </ActionButton>
                 <TooltipWrapper tooltipText={!enableLinkedinAction ? "You need to be logged in to LinkedIn and have the extension installed to like and skip posts" : ''}> 
                    <div> {/* pointer events preventing tooltip from being shown */}
                        <ActionButton disabled={!enableLinkedinAction} onClick={handleLikeAndSkipPost}>
                            <SvgFragment type="emptyLike" />
                            <PostActionText>Like & Skip</PostActionText>
                        </ActionButton>
                    </div>
                </TooltipWrapper>
                {showHoverPopup && (
                    <HoverPopup onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                        {[
                            { type: 'like', reaction: 'LIKE' },
                            { type: 'praise', reaction: 'PRAISE' },
                            { type: 'appreciation', reaction: 'APPRECIATION' },
                            { type: 'empathy', reaction: 'EMPATHY' },
                            { type: 'interest', reaction: 'INTEREST' },
                            { type: 'entertainment', reaction: 'ENTERTAINMENT' }
                        ].map((item, index) => (
                            <HoverIcon key={index} onClick={(e: any) => handlePostLike(e, item.reaction)}>
                                <SvgFragment type={item.type} />
                            </HoverIcon>
                        ))}
                    </HoverPopup>
                )}
            </ActionButtonsSection>

            <CommentSection>
                <TooltipWrapper placement='top' tooltipText={!enableLinkedinAction ? "You need to be logged in to LinkedIn and have the extension installed to add comments" : ''}>
                    <div> {/* pointer events preventing tooltip from being shown */}
                    <CommentInputContainer disabled={!enableLinkedinAction}>
                        <CommentInput
                            ref={commentInputRef}
                            placeholder="Add a comment..."
                            value={commentText}
                            onChange={(e) => {
                                const raw = e.target.value
                                const replaced = convertEmoticonsToEmoji(raw)
                                if (replaced !== raw) {
                                    e.target.value = replaced
                                }
                                setCommentText(replaced)
                                if (!commentInputRef.current) return
                                requestAnimationFrame(() => {
                                    if (!commentInputRef.current) return
                                    autosizeTextarea(commentInputRef.current)
                                })
                            }}
                            rows={1}
                        />
                        <ActionButtons gap='4px'>
                            <EmojiPicker onSelect={(emoji: string) => setCommentText(commentText + emoji)}/>

                            <SvgWrapper onClick={localSparkleLoading ? undefined : () => setChooseTemplateModal((message: string) => {
                                const normalized = normalizeMessageForTextarea(replaceProfilePlaceholders(message))
                                setCommentText(normalized)
                                if (commentInputRef.current) {
                                    commentInputRef.current.focus()
                                    requestAnimationFrame(() => {
                                        if (!commentInputRef.current) return
                                        autosizeTextarea(commentInputRef.current)
                                    })
                                }
                            })}>
                                <SvgFragment type='templates'/>
                            </SvgWrapper>

                            {localSparkleLoading
                                ? <SvgWrapper style={{ position: 'relative', bottom: '-5px' }}><LoadingSpinner height="15px" width="15px" /></SvgWrapper>
                                : <Assistant content={commentText} setContent={setCommentText} postCard onLoadingChange={setLocalSparkleLoading}/>
                            }
                        </ActionButtons>
                    </CommentInputContainer>
                    </div>
                </TooltipWrapper>
                <ReplyButtonContainer>
                    <ReplyButtonWrapper ref={dropdownRef} disabled={commentText === ''}>
                        <ReplyButton onClick={() => handleCommentProfilePosts(true)}>Reply and Like</ReplyButton>
                        <ReplyDropdownSelect onClick={handleReplyDropdownToggle}>⋯</ReplyDropdownSelect>
                        {showReplyDropdown && (
                            <ReplyDropdownMenu>
                                <ReplyDropdownItem onClick={() => handleCommentProfilePosts(false)}>
                                    Reply
                                </ReplyDropdownItem>
                            </ReplyDropdownMenu>
                        )}
                    </ReplyButtonWrapper>
                </ReplyButtonContainer>
            </CommentSection>

            {/* User Comments Section */}
            {/* TODO: Remove false when LD-861 is merged */}
            {localComments && localComments.length > 0 && (
                <UserCommentsSection>
                    <CommentsHeader>
                        <FormControl $orientation="column">
                            <Select
                                isSearchable={false}
                                options={commentsSortOptions}
                                value={commentsSortBy}
                                onChange={handleCommentsSortChange}
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        border: 'none',
                                        boxShadow: 'none',
                                        backgroundColor: 'transparent',
                                        minHeight: 'auto',
                                        height: 'auto',
                                        padding: 0,
                                        width: 'fit-content',
                                        display: 'inline-flex',
                                        '&:hover': {
                                            border: 'none',
                                            boxShadow: 'none',
                                            cursor: 'pointer'
                                        },
                                        '&:focus': {
                                            border: 'none',
                                            boxShadow: 'none',
                                            cursor: 'pointer'
                                        }
                                    }),
                                    container: (base) => ({
                                        ...base,
                                        width: '240px'
                                    }),
                                    valueContainer: (base) => ({
                                        ...base,
                                        padding: 0,
                                        justifyContent: 'flex-start',
                                        flex: 'none'
                                    }),
                                    indicatorsContainer: (base) => ({
                                        ...base,
                                        padding: 0,
                                        marginLeft: '4px',
                                        flex: 'none'
                                    }),
                                    dropdownIndicator: (base, state) => ({
                                        ...base,
                                        padding: 0,
                                        margin: 0,
                                        color: styles.colors.black300,
                                        transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s ease'
                                    }),
                                    option: (base, state) => ({
                                        ...base,
                                        backgroundColor: 'transparent',
                                        color: state.isSelected ? '#4B5563' : styles.colors.black600,
                                        '&:hover': {
                                            cursor: 'pointer',
                                            backgroundColor: 'transparent',
                                            color: styles.colors.primary600
                                        },
                                        '&:active': {
                                            cursor: 'pointer',
                                            backgroundColor: 'transparent',
                                            color: styles.colors.primary600
                                        }
                                    }),
                                    menu: (base) => ({
                                        ...base,
                                        backgroundColor: 'white',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '4px',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                                    })
                                }}
                            />
                        </FormControl>
                    </CommentsHeader>
                    {getSortedComments().map((comment: any, index: number) => {
                        const commentId = comment._id || comment.commentUrn || index.toString()
                        const isExpanded = isCommentExpanded(commentId)
                        const shouldTruncate = shouldTruncateComment(comment.text)
                        const displayText = shouldTruncate && !isExpanded
                            ? comment.text.substring(0, 140) + '...'
                            : comment.text
                        const isEditing = editingCommentId === commentId

                        return (
                            <CommentItem key={commentId}>
                                <CommentHeader>
                                    <ProfilePhoto
                                        source={comment.user?.profilePicture}
                                        capitalLetters={`${comment.user?.firstName?.charAt(0)?.toUpperCase() || ''}${comment.user?.lastName?.charAt(0)?.toUpperCase() || ''}`}
                                        tooltipMessage={`${comment.user?.firstName} ${comment.user?.lastName}`}
                                        width='32px'
                                        height='32px'
                                    />
                                    <CommentAuthorInfo>
                                        <Text $paragraph2 $bold $active>{comment.user?.firstName} {comment.user?.lastName}</Text>
                                        <CommentMetaRow>
                                            {comment.user?.occupation && (
                                                <TooltipWrapper tooltipText={comment.user.occupation}>
                                                    <OccupationText>
                                                        {comment.user.occupation}
                                                    </OccupationText>
                                                </TooltipWrapper>
                                            )}
                                            {comment.user?.occupation && comment.createdAt && (
                                                <span style={{ flexShrink: 0 }}> &nbsp;•&nbsp; </span>
                                            )}
                                            {comment.createdAt && (
                                                <DateText>
                                                    {formatTimeAgo('', comment.createdAt)}
                                                </DateText>
                                            )}
                                        </CommentMetaRow>
                                    </CommentAuthorInfo>
                                    {!isEditing && comment.user?._id === user?._id && (
                                        <CommentActions>
                                            <EditDeleteActionDropdown
                                                object={comment}
                                                type="comment"
                                                title="Edit"
                                                deleteTitle="Delete"
                                                onDeleteHandler={enableLinkedinAction ? () => handleDeleteComment(commentId) : (e: any) => {}}
                                                onEditHandler={enableLinkedinAction ? () => handleEditComment(commentId) : (e: any) => {}}
                                                disabled={!enableLinkedinAction}
                                                tooltipMessage={!enableLinkedinAction ? "You need to be logged in to LinkedIn and have the extension installed to edit/delete comments" : 'Actions'}
                                            />
                                        </CommentActions>
                                    )}
                                </CommentHeader>
                                <CommentText>
                                    {isEditing ? (
                                        <EditCommentContainer>
                                            <CommentInputContainer>
                                                <CommentInput
                                                    ref={editCommentRef}
                                                    placeholder="Edit your comment..."
                                                    value={editingCommentText}
                                                    onChange={(e) => {
                                                        const raw = e.target.value
                                                        const replaced = convertEmoticonsToEmoji(raw)
                                                        if (replaced !== raw) {
                                                            e.target.value = replaced
                                                        }
                                                        setEditingCommentText(replaced)
                                                        if (!editCommentRef.current) return
                                                        requestAnimationFrame(() => {
                                                            if (!editCommentRef.current) return
                                                            autosizeTextarea(editCommentRef.current)
                                                        })
                                                    }}
                                                    rows={1}
                                                />
                                                <ActionButtons gap='4px'>
                                                    <EmojiPicker onSelect={(emoji: string) => setEditingCommentText(editingCommentText + emoji)}/>

                                                    <SvgWrapper onClick={editCommentSparkleLoading ? undefined : () => setChooseTemplateModal((message: string) => {
                                                        const normalized = normalizeMessageForTextarea(replaceProfilePlaceholders(message))
                                                        setEditingCommentText(normalized)
                                                        if (editCommentRef.current) {
                                                            editCommentRef.current.focus()
                                                            requestAnimationFrame(() => {
                                                                if (!editCommentRef.current) return
                                                                autosizeTextarea(editCommentRef.current)
                                                            })
                                                        }
                                                    })}>
                                                        <SvgFragment type='templates'/>
                                                    </SvgWrapper>

                                                    {editCommentSparkleLoading
                                                        ? <SvgWrapper style={{ position: 'relative', bottom: '-5px' }}><LoadingSpinner height="15px" width="15px" /></SvgWrapper>
                                                        : <Assistant content={editingCommentText} setContent={setEditingCommentText} postCard onLoadingChange={setEditCommentSparkleLoading}/>
                                                    }
                                                </ActionButtons>
                                            </CommentInputContainer>
                                            <EditCommentButtons>
                                                <CancelButton onClick={handleCancelEdit}>
                                                    Cancel
                                                </CancelButton>
                                                <SaveButton onClick={handleSaveEdit}>
                                                    Save
                                                </SaveButton>
                                            </EditCommentButtons>
                                        </EditCommentContainer>
                                    ) : (
                                        <StyledText $paragraph2>
                                            {displayText}
                                            {shouldTruncate && (
                                                <ActionText $regular onClickHandler={() => handleToggleCommentExpansion(commentId)}>
                                                    {isExpanded ? ' Show less' : ' Show more'}
                                                </ActionText>
                                            )}
                                        </StyledText>
                                    )}
                                </CommentText>
                            </CommentItem>
                        )
                    })}
                    {getRemainingCommentsCount() > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '12px' }}>
                            <ActionText $regular onClickHandler={handleShowMoreComments}>
                                Show {getRemainingCommentsCount()} more comments
                            </ActionText>
                        </div>
                    )}
                    {visibleCommentsCount > 1 && visibleCommentsCount === localComments.length && (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '12px' }}>
                            <ActionText onClickHandler={handleShowLessComments}>
                                Show less
                            </ActionText>
                        </div>
                    )}
                </UserCommentsSection>
            )}
        </Container>
    )
}

const Container = styled.div`
    background: ${styles.colors.white};
    border: 1px solid #E0E0E0;
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    position: relative;
`

const HeaderSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;
`

const PostContent = styled.div`
    margin: 12px 0;
    padding: 12px 0;
`

const HeadlineText = styled(Text)`
    color: #666;
    font-size: 14px;
    font-weight: 400;
    display: block;
    max-width: 100%;
`

const HeadlineContent = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    gap: 0;
    min-width: 0;
`

const PostTextWithHTML = styled.div`
    line-height: 1.5;
    font-size: 16px;
    color: #333;
    font-weight: 400;
    margin-bottom: 8px;
    white-space: pre-wrap;
`

const ExpandableTextContainer = styled.div<{ $isExpanded: boolean; $showExpandButton: boolean }>`
    position: relative;
    max-height: ${({ $isExpanded, $showExpandButton }) => 
        $showExpandButton && !$isExpanded ? '400px' : '3000px'};
    overflow: hidden;
    transition: max-height 0.5s ease;
`

const FadeOverlay = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 80px;
    background: linear-gradient(transparent, ${styles.colors.white});
    pointer-events: none;
    opacity: 1;
    transition: opacity 0.3s ease;
`

const ShowMoreButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    background: none;
    border: none;
    color: ${styles.colors.primary600};
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    padding: 8px 0;
    margin-top: 8px;
    transition: all 0.2s ease;

    &:hover {
        color: ${styles.colors.primary600};
        transform: translateY(-1px);
        opacity: 0.8;
    }

    svg {
        width: 16px;
        height: 16px;
        transition: transform 0.2s ease;
        
        path {
            fill: ${styles.colors.primary600};
        }
    }

    &:hover svg {
        transform: scale(1.1);
    }
`

const ShowMoreText = styled.span`
    color: ${styles.colors.primary600};
    font-size: 14px;
    font-weight: 500;
`

const RotatedArrow = styled.span`
    display: inline-flex;
    transform: rotate(180deg);
    transition: transform 0.3s ease;
`

const RepostedTextWithHTML = styled.div`
    line-height: 1.5;
    font-size: 15px;
    color: #333;
    font-weight: 400;
    margin-bottom: 12px;
    white-space: pre-wrap;
`

const StatText = styled(Text)`
    color: ${styles.colors.black300};
    font-size: 12px;
    font-weight: 400;
`

const PostImage = styled.img`
    width: 100%;
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin-top: 12px;
    border: 1px solid ${styles.colors.black200};
    cursor: pointer;
    transition: transform 0.2s ease;

    &:hover {
        transform: scale(1.02);
    }
`

const PostVideo = styled.video`
    width: 100%;
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin-top: 12px;
    border: 1px solid ${styles.colors.black200};
    transition: transform 0.2s ease;

    &:hover {
        transform: scale(1.02);
    }
`


const PostArticleContainer = styled.div`
    display: flex;
    align-items: center;
    margin-top: 12px;
    padding: 12px;
    background-color: ${styles.colors.white};
    border: 1px solid ${styles.colors.black100};
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`

const PostArticleImage = styled.div`
    min-width: 150px;
    width: 150px;
    background-color: ${styles.colors.black100};
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
    flex-shrink: 0;
    overflow: hidden;
`

const PostArticleThumb = styled.img`
    width: 100%;
    height: auto;
    object-fit: cover;
    border-radius: 8px;
`

const PostArticleImagePlaceholder = styled.div`
    font-size: 24px;
    color: ${styles.colors.black400};
`

const PostArticleContent = styled.div`
    flex-grow: 1;
`

const PostArticleTitle = styled(Text)`
    font-size: 16px;
    font-weight: bold;
    color: ${styles.colors.black600};
    margin-bottom: 4px;
`;

const PostArticleDescription = styled(Text)`
    font-size: 14px;
    color: ${styles.colors.black600};
    line-height: 1.4;
    margin-bottom: 8px;
`;

const PostArticleSource = styled(Text)`
    font-size: 12px;
    color: ${styles.colors.black400};
`;


const ActionButton = styled.div<{ width?: string, reacted?: boolean, disabled?: boolean, fontSize?: string, fontWeight?: string, linkAction?: boolean }>`
    ${({ disabled }) => disabled && `
        opacity: 0.5;
        pointer-events: none;
    `}
    width: ${({width}) => width ?? '100px'};
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    ${({ fontSize }) => fontSize && `font-size: ${fontSize};`}
    ${({ fontWeight }) => fontWeight && `font-wize: ${fontWeight};`}
    &:hover {
         color: ${styles.colors.primary600};
    } 

    ${({ reacted }) => reacted && `
        path { fill: ${styles.colors.primary600}; }
    `}

    ${({ linkAction }) => linkAction && `
        cursor: pointer;
        font-weight: 700;
        color: ${styles.colors.primary600};
        font-size: 13px;
        margin-top: -2px;
        &:hover {
            color: ${styles.colors.primary600};
            opacity: 0.8;
        }
    `}
`

const PostActionText = styled.span`
    font-size: 14px;
`

const CommentSection = styled.div`
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #E0E0E0;
`

const CommentInputContainer = styled.div<{ disabled?: boolean }>`
    ${({disabled}) => disabled && `
        pointer-events: none;
    `}
    position: relative;
    margin-bottom: 12px;
`

const CommentInput = styled.textarea`
    width: 100%;
    height: 50px;
    padding: 12px 75px 12px 16px;
    border: 1px solid #E0E0E0;
    border-radius: 8px;
    font-size: 14px;
    color: #333;
    background: white;
    outline: none;
    transition: border-color 0.2s ease;
    min-height: 50px;
    max-height: 120px;
    resize: none;
    overflow: auto;

    &::placeholder {
        color: #999;
    }

    &:focus {
        border-color: ${styles.colors.primary600};
    }
`

const ReplyButtonContainer = styled.div`
    display: flex;
    position: relative;
    justify-content: flex-end;
`

const ReplyButtonWrapper = styled.div<{ disabled: boolean }>`
    ${({ disabled }) => disabled && `
        opacity: 0.5;
        pointer-events: none;
    `}

    position: relative;
    width: 162px;
    height: 40px;
    display: flex
`

const ReplyButton = styled.button`
    width: 124px;
    height: 40px;
    background: ${styles.colors.primary600};
    border-radius: 4px 0 0 4px;
    color: ${styles.colors.white};
    font-size: 14px;
    font-weight: 500;
`

const ReplyDropdownSelect = styled.div`
    width: 38px;
    height: 40px;
    border: 1px solid ${styles.colors.primary600};
    border-radius: 0 4px 4px 0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    position: relative;
`

const ReplyDropdownItem = styled.button`
    width: 100%;
    padding: 12px 16px;
    font-size: 14px;
    color: ${styles.colors.black600};
    cursor: pointer;
    transition: background-color 0.2s ease;
    background-color: white;
    text-align: left;

    &:hover {
        background-color: ${styles.colors.black100} !important;
    }
`

const HoverPopup = styled.div`
    position: absolute;
    top: -50px;
    background: ${styles.colors.white};
    border: 1px solid ${styles.colors.black200};
    border-radius: 8px;
    padding: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    display: flex;
    gap: 12px;
    z-index: 10;
`

const HoverIcon = styled.div`
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: ${styles.colors.black100};
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: ${styles.colors.primary200};
        transform: scale(1.1);
    }

    svg {
        width: 16px;
        height: 16px;
    }
`

const ActionButtonsSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 0 8px 0;
    border-top: 1px solid #E0E0E0;
    position: relative;
`

const PostStats = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 0;
    border-top: 1px solid #E0E0E0;
`

const ReplyDropdownMenu = styled.div`
    position: absolute;
    top: 100%;
    right: 0;
    background: ${styles.colors.white};
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1;
    min-width: 162px;
    overflow: hidden;
    margin-top: 4px;
`

const SvgWrapper = styled.div`
    cursor: pointer;

    & svg {
        width: 20px;
        height: 20px;

        g {
            transform: translate(0px, 0px) scale(1.2);
            transform-origin: center;
            transform-box: fill-box;
        }
    }

    &:hover svg {
        path {
            fill: ${styles.colors.primary600};   
        }
    }
`

const ActionButtons = styled(Row)`
    position: absolute;
    right: 14px;
    bottom: 20px;
`

// Reshare/Repost styled components
const RepostContainer = styled.div`
    margin-top: 16px;
    border: 1px solid ${styles.colors.black200};
    border-radius: 8px;
    background-color: ${styles.colors.white};
`

const RepostIndicator = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
`

const RepostedContent = styled.div`
    padding: 16px;
`

const RepostedImage = styled.img`
    width: 100%;
    max-width: 100%;
    height: auto;
    border-radius: 6px;
    margin-top: 8px;
    cursor: pointer;
    transition: transform 0.2s ease;

    &:hover {
        transform: scale(1.01);
    }
`

const RepostedVideo = styled.video`
    width: 100%;
    max-width: 100%;
    height: auto;
    border-radius: 6px;
    margin-top: 8px;
    transition: transform 0.2s ease;

    &:hover {
        transform: scale(1.01);
    }
`

const RepostedPdfContainer = styled.div`
    margin-top: 8px;
    padding: 8px;
    background-color: ${styles.colors.black100};
    border: 1px solid ${styles.colors.black100};
    border-radius: 6px;
`

const RepostedPdfIframe = styled.iframe`
    width: 100%;
    height: 400px;
    border: none;
    border-radius: 4px;
`

const RepostedArticleContainer = styled.div`
    display: flex;
    align-items: center;
    margin-top: 8px;
    padding: 12px;
    background-color: ${styles.colors.black100};
    border: 1px solid ${styles.colors.black100};
    border-radius: 6px;
`

const RepostedArticleImage = styled.div`
    min-width: 150px;
    width: 150px;
    background-color: ${styles.colors.black100};
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
    flex-shrink: 0;
    overflow: hidden;
`

const RepostedArticleThumb = styled.img`
    width: 100%;
    height: auto;
    object-fit: cover;
    border-radius: 6px;
`

const RepostedArticleImagePlaceholder = styled.div`
    font-size: 20px;
    color: ${styles.colors.black400};
`

const RepostedArticleContent = styled.div`
    flex-grow: 1;
`

const RepostedArticleTitle = styled(Text)`
    font-size: 14px;
    font-weight: 600;
    color: ${styles.colors.black600};
    margin-bottom: 2px;
`

const RepostedArticleDescription = styled(Text)`
    font-size: 13px;
    color: ${styles.colors.black500};
    line-height: 1.3;
    margin-bottom: 4px;
`

const RepostedArticleSource = styled(Text)`
    font-size: 11px;
    color: ${styles.colors.black400};
`

const RepostedPollContainer = styled.div`
    margin-top: 8px;
    padding: 12px;
    background-color: ${styles.colors.black100};
    border: 1px solid ${styles.colors.black100};
    border-radius: 6px;
`

const CelebrationContainer = styled.div`
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 10px;
    background-color: ${styles.colors.white};
    border: 1px solid ${styles.colors.black200};
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`

const CelebrationImage = styled.img`
    width: 100%;
    height: auto;
    border-radius: 8px;
    margin-bottom: 8px;
    cursor: pointer;
    transition: transform 0.2s ease;

    &:hover {
        transform: scale(1.02);
    }
`

const CelebrationHeadline = styled(Text)`
    font-size: 18px;
    font-weight: 600;
    color: ${styles.colors.black600};
    text-align: center;
    line-height: 1.4;
`

// Document Carousel Components
const DocumentCarouselContainer = styled.div<{ $isFullscreen: boolean }>`
    margin-top: 12px;
    background: ${styles.colors.white};
    border: 1px solid ${styles.colors.black200};
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    position: relative;
    overflow: hidden;
    ${({ $isFullscreen }) => $isFullscreen && `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 1000;
        border-radius: 0;
        margin: 0;
        background: #000;
        display: flex;
        flex-direction: column;
    `}
`

const CarouselHeader = styled.div<{ $isFullscreen?: boolean }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background: ${({ $isFullscreen }) => $isFullscreen ? '#000' : styles.colors.black100};
    border-bottom: 1px solid ${({ $isFullscreen }) => $isFullscreen ? '#333' : styles.colors.black200};
    color: ${({ $isFullscreen }) => $isFullscreen ? '#fff' : 'inherit'};
`

const SlideCounter = styled(Text)<{ $isFullscreen?: boolean }>`
    font-size: 14px;
    font-weight: 500;
    color: ${({ $isFullscreen }) => $isFullscreen ? '#fff' : styles.colors.black600};
`

const CarouselControls = styled.div`
    display: flex;
    gap: 8px;
`

const ControlButton = styled.button<{ $isFullscreen?: boolean }>`
    width: 32px;
    height: 32px;
    border: none;
    background: ${({ $isFullscreen }) => $isFullscreen ? '#333' : styles.colors.white};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    &:hover {
        background: ${({ $isFullscreen }) => $isFullscreen ? '#555' : styles.colors.primary100};
        transform: scale(1.05);
    }

    svg {
        width: 16px;
        height: 16px;
        path {
            fill: ${({ $isFullscreen }) => $isFullscreen ? '#fff' : styles.colors.black600};
        }
    }
`

const CarouselContent = styled.div<{ $isFullscreen?: boolean }>`
    position: relative;
    display: flex;
    align-items: center;
    min-height: ${({ $isFullscreen }) => $isFullscreen ? '100vh' : '500px'};
    flex: 1;
    background: ${({ $isFullscreen }) => $isFullscreen ? '#000' : 'transparent'};
`

const NavigationButton = styled.button<{ $position: 'left' | 'right'; $isFullscreen?: boolean }>`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    ${({ $position }) => $position === 'left' ? 'left: 16px;' : 'right: 16px;'}
    width: 40px;
    height: 40px;
    border: none;
    background: ${({ $isFullscreen }) => $isFullscreen ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.7)'};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    z-index: 10;

    &:hover {
        background: ${({ $isFullscreen }) => $isFullscreen ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.9)'};
        transform: translateY(-50%) scale(1.1);
    }

    svg {
        width: 20px;
        height: 20px;
        path {
            fill: white;
        }
    }
`

const SlidesContainer = styled.div<{ $isFullscreen?: boolean }>`
    flex: 1;
    overflow: hidden;
    position: relative;
    padding: ${({ $isFullscreen }) => $isFullscreen ? '10px' : '20px'};
`

const SlidesWrapper = styled.div<{ $currentSlide: number; $isFullscreen?: boolean }>`
    display: flex;
    width: 400%; /* 4 slides * 100% */
    height: ${({ $isFullscreen }) => $isFullscreen ? '95vh' : '500px'};
    transform: translateX(-${props => props.$currentSlide * 25}%); /* Move by 25% for each slide */
    transition: transform 0.3s ease-in-out;
`

const DocumentSlide = styled.div<{ $isFullscreen?: boolean }>`
    width: 25%; /* Each slide takes 25% of the wrapper (100% / 4 slides) */
    height: 100%;
    flex-shrink: 0;
    padding: ${({ $isFullscreen }) => $isFullscreen ? '0 10px' : '0 10px'};
    display: flex;
    justify-content: center;
    align-items: center;
`


const CarouselFooter = styled.div<{ $isFullscreen?: boolean }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background: ${({ $isFullscreen }) => $isFullscreen ? '#000' : styles.colors.black100};
    border-top: 1px solid ${({ $isFullscreen }) => $isFullscreen ? '#333' : styles.colors.black200};
    color: ${({ $isFullscreen }) => $isFullscreen ? '#fff' : 'inherit'};
`

const ProgressBar = styled.div<{ $isFullscreen?: boolean }>`
    flex: 1;
    height: 4px;
    background: ${({ $isFullscreen }) => $isFullscreen ? '#333' : styles.colors.black200};
    border-radius: 2px;
    overflow: hidden;
    margin-right: 16px;
`

const ProgressFill = styled.div<{ $progress: number }>`
    height: 100%;
    width: ${({ $progress }) => $progress}%;
    background: ${styles.colors.primary600};
    border-radius: 2px;
    transition: width 0.3s ease;
`

const SwipeHint = styled(Text)<{ $isFullscreen?: boolean }>`
    font-size: 12px;
    color: ${({ $isFullscreen }) => $isFullscreen ? '#999' : styles.colors.black500};
    display: flex;
    align-items: center;
    gap: 4px;
`



// PDF Canvas Components
const PDFLoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 500px;
    gap: 16px;
`

const PDFCanvas = styled.canvas<{ $isFullscreen?: boolean }>`
    width: 100%;
    height: 100%;
    max-width: ${({ $isFullscreen }) => $isFullscreen ? '95vw' : '600px'};
    max-height: ${({ $isFullscreen }) => $isFullscreen ? '95vh' : '800px'};
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    background: transparent;
    object-fit: contain;
    position: relative;
    z-index: 2;
`

const PDFIframeFallback = styled.iframe`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
    border-radius: 8px;
    background: white;
    z-index: 1;
    opacity: 0;
    pointer-events: none;
    
    /* Show iframe if canvas is empty */
    ${PDFCanvas}:not([data-has-content="true"]) + & {
        opacity: 1;
        pointer-events: auto;
    }
`

const PDFErrorContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 500px;
    gap: 16px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    padding: 40px;
`

const PDFErrorText = styled(Text)`
    color: ${styles.colors.black600};
    font-size: 16px;
    text-align: center;
    line-height: 1.5;
`

const PDFDownloadButton = styled.a`
    display: inline-block;
    padding: 12px 24px;
    background-color: ${styles.colors.primary600};
    color: ${styles.colors.white};
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    text-decoration: none;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
        background-color: ${styles.colors.primary500};
    }
`

// Simple Image Carousel Component
interface IImageCarouselProps {
    images: string[];
}

const ImageCarousel: FunctionComponent<IImageCarouselProps> = ({ images }) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const nextSlide = useCallback(() => {
        setCurrentSlide((prev) => (prev + 1) % images.length);
    }, [images.length]);

    const prevSlide = useCallback(() => {
        setCurrentSlide((prev) => (prev - 1 + images.length) % images.length);
    }, [images.length]);

    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isFullscreen) return;
            
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                prevSlide();
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                nextSlide();
            } else if (e.key === 'Escape') {
                e.preventDefault();
                setIsFullscreen(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isFullscreen, nextSlide, prevSlide]);

    if (images.length === 0) return null;

    return (
        <div style={{ 
            position: 'relative', 
            width: '100%', 
            margin: '8px 0',
            borderRadius: '8px',
            overflow: 'hidden',
            background: 'white',
            border: '1px solid #E0E0E0'
        }}>
            {/* Header */}
            {/* <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '12px 16px',
                borderBottom: '1px solid #E0E0E0'
            }}>
                <div style={{ fontSize: '14px', fontWeight: '500' }}>
                    {images.length} images
                </div>
                <button 
                    onClick={toggleFullscreen}
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px'
                    }}
                >
                    <SvgFragment type="expand" />
                </button>
            </div> */}

            {/* Grid Layout */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr',
                gridTemplateRows: '1fr 1fr 1fr',
                gap: '2px',
                height: '300px'
            }}>
                {/* Left side - First image (large, spans 3 rows) */}
                <div 
                    style={{
                        position: 'relative',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        gridRow: '1 / 4'
                    }}
                    onClick={() => {
                        setCurrentSlide(0);
                        setIsFullscreen(true);
                    }}
                >
                        <img 
                            src={images[0]} 
                            alt="1"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                transition: 'transform 0.2s ease',
                                cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                            }}
                        />
                </div>

                {/* Right side - Next 3 images (small) */}
                {images.slice(1, 4).map((image, index) => (
                    <div 
                        key={index + 1}
                        style={{
                            position: 'relative',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            gridColumn: '2',
                            gridRow: `${index + 1}`
                        }}
                        onClick={() => {
                            setCurrentSlide(index + 1);
                            setIsFullscreen(true);
                        }}
                    >
                        <img 
                            src={image} 
                            alt={`${index + 2}`}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                transition: 'transform 0.2s ease',
                                cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                            }}
                        />
                        {index === 2 && images.length > 4 && (
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'rgba(0,0,0,0.7)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '18px',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                            }}>
                                +{images.length - 4}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Fullscreen Modal */}
            {isFullscreen && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: '#000',
                    zIndex: 1000,
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {/* Fullscreen Header */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '16px',
                        color: 'white',
                        borderBottom: '1px solid #333'
                    }}>
                        <div>{currentSlide + 1} / {images.length}</div>
                        <button 
                            onClick={toggleFullscreen}
                            style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: 'white',
                                padding: '4px'
                            }}
                        >
                            <SvgFragment type="expand" />
                        </button>
                    </div>

                    {/* Fullscreen Content */}
                    <div style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative'
                    }}>
                        <button 
                            onClick={prevSlide}
                            style={{
                                position: 'absolute',
                                left: '20px',
                                background: 'rgba(255,255,255,0.2)',
                                border: 'none',
                                borderRadius: '50%',
                                width: '40px',
                                height: '40px',
                                cursor: 'pointer',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <SvgFragment type="arrowLeft" />
                        </button>

                        <img 
                            src={images[currentSlide]} 
                            alt={`${currentSlide + 1}`}
                            style={{
                                maxWidth: '90vw',
                                maxHeight: '85vh',
                                objectFit: 'contain'
                            }}
                        />

                        <button 
                            onClick={nextSlide}
                            style={{
                                position: 'absolute',
                                right: '20px',
                                background: 'rgba(255,255,255,0.2)',
                                border: 'none',
                                borderRadius: '50%',
                                width: '40px',
                                height: '40px',
                                cursor: 'pointer',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <SvgFragment type="arrowRight" />
                        </button>
                    </div>

                    {/* Fullscreen Footer */}
                    <div style={{
                        padding: '16px',
                        color: 'white',
                        textAlign: 'center',
                        borderTop: '1px solid #333'
                    }}>
                        <div style={{
                            width: '100%',
                            height: '4px',
                            background: '#333',
                            borderRadius: '2px',
                            marginBottom: '8px'
                        }}>
                            <div style={{
                                width: `${((currentSlide + 1) / images.length) * 100}%`,
                                height: '100%',
                                background: '#5964ff',
                                borderRadius: '2px'
                            }} />
                        </div>
                        <div style={{ fontSize: '12px', color: '#999' }}>
                            Swipe or use arrow keys to navigate
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// User Comments Styled Components
const UserCommentsSection = styled.div`
    margin-top: 16px;
`;

const CommentsHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    gap: 16px;
`;

const CommentItem = styled.div`
    margin-bottom: 16px;
    padding: 12px;
    border-radius: 8px;
    border: 1px solid ${styles.colors.black100};
`;

const CommentHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
`;

const CommentAuthorInfo = styled.div`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

const CommentActions = styled.div`
    display: flex;
    align-items: center;
    opacity: 1;
`;


const CommentText = styled.div`
    line-height: 1.4;
    white-space: pre-wrap;
    word-break: break-word;
`;

const CommentMetaRow = styled.div`
    display: flex;
    align-items: center;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0%;
    vertical-align: middle;
    color: #4B5563;
    max-width: 100%;
    gap: 0;
    min-width: 0;
`;

const OccupationText = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 0 1 auto;
    min-width: 0;
    max-width: 100%;
`;

const DateText = styled.span`
    flex-shrink: 0;
    white-space: nowrap;
`;



const StyledText = styled(Text)`
  display: inline-block;
  text-align: left;

  & > * {
    display: inline-block;
  }
`;

const EditCommentContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;


const EditCommentButtons = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
`;

const CancelButton = styled.button`
    padding: 8px 16px;
    border: 1px solid #E0E0E0;
    border-radius: 6px;
    background: white;
    color: #666;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: #f5f5f5;
        border-color: #ccc;
    }
`;

const SaveButton = styled.button`
    padding: 8px 16px;
    border: none;
    border-radius: 6px;
    background: ${styles.colors.primary600};
    color: white;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: ${styles.colors.primary500};
    }

    &:disabled {
        background: #ccc;
        cursor: not-allowed;
    }
`;

const HeadlineOccupationText = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 0 1 auto;
    min-width: 0;
    max-width: 100%;
`;

const HeadlineDateText = styled.span`
    flex-shrink: 0;
    white-space: nowrap;
`;

export default PostCard;
